import { onMounted, onBeforeUnmount } from 'vue';
import { drawCoveredImage } from '@/utils/drawCoveredImage';

// Define screen orientation type
type Orientation = 'portrait' | 'landscape';

// Configurable options for the scroll video behavior
interface ScrollVideoOptions {
  frameCount?: number;
  assetDirectory?: string;
  assetNameLandscape?: string;
  assetNamePortrait?: string;
  assetNumberDepth?: number;
  assetType?: string;
  preloadRootMargin?: string;
}

// Main composable function for scroll-driven video playback
export const useScrollVideo = (
  wrapperRef: Ref<HTMLElement | null>,
  canvasRef: Ref<HTMLCanvasElement | null>,
  frameCount: number,
  options: ScrollVideoOptions = {},
) => {
  // Merge default settings with passed options
  const settings = {
    assetDirectory: '/images/intro-frames/',
    assetNameLandscape: 'intro-frame-landscape-',
    assetNamePortrait: 'intro-frame-portrait-',
    assetNumberDepth: 4,
    assetType: 'avif',
    preloadRootMargin: '50%',
    ...options,
  };

  // State and DOM references
  let wrapper: HTMLElement | null = null;
  let canvas: HTMLCanvasElement | null = null;
  let context: CanvasRenderingContext2D | null = null;
  let orientation: Orientation | null = null;
  let previousOrientation: Orientation | null = null;
  let canvasWidth = 0;
  let canvasHeight = 0;
  let frameIndex = 0;
  const images: Record<string, HTMLImageElement> = {}; // Preloaded images
  let isTouchDevice = false;
  let preloadObserver: IntersectionObserver | null = null;
  let scrollObserver: IntersectionObserver | null = null;
  let hasPreloaded = false;
  let loadedImageCount = 0;
  let totalToLoad = 0;
  let allowScrollPlayback = false;
  let ticking = false; // Scroll throttling flag

  // Constructs an image path string from the frame index
  const getImagePath = (index: number): string => {
    const dir = settings.assetDirectory;
    const name =
      orientation === 'portrait'
        ? settings.assetNamePortrait
        : settings.assetNameLandscape;
    const padded = index.toString().padStart(settings.assetNumberDepth, '0');
    return `${dir}${name}${padded}.${settings.assetType}`;
  };

  // Load and draw the first frame (called early for fast visual feedback)
  const preloadFirstImage = async () => {
    setOrientation();
    const path = getImagePath(1);
    const img = new Image();
    img.src = path;
    images[path] = img;

    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(`Image failed to load: ${path}`);
    });

    frameIndex = 1;
    requestAnimationFrame(updateImage);
  };

  // Preload all remaining frames (starts lazily via IntersectionObserver)
  const preloadImages = async (): Promise<void> => {
    if (hasPreloaded) return;
    hasPreloaded = true;

    totalToLoad = frameCount;
    loadedImageCount = 0;

    const promises: Promise<void>[] = [];

    for (let i = 2; i <= totalToLoad; i++) {
      const path = getImagePath(i);
      if (!images[path]) {
        const img = new Image();
        img.src = path;
        images[path] = img;

        const promise = new Promise<void>((resolve, reject) => {
          img.onload = () => {
            loadedImageCount++;
            // Enable scroll-driven frame switching when 50% of frames are ready
            if (!allowScrollPlayback && loadedImageCount / totalToLoad >= 0.5) {
              allowScrollPlayback = true;
            }
            resolve();
          };
          img.onerror = () => reject(`Image failed to load: ${path}`);
        });

        promises.push(promise);
      }
    }

    await Promise.all(promises);
  };

  // Draws the currently selected frame onto the canvas
  const updateImage = (): void => {
    if (!context || !canvas) return;
    const image = images[getImagePath(frameIndex)];
    if (image?.complete) {
      drawCoveredImage(context, image, 0, 0, canvasWidth, canvasHeight);
    }
  };

  // Resizes canvas to match screen dimensions
  const setCanvasSize = () => {
    if (!canvas) return;
    if (typeof window !== 'undefined') {
      canvasWidth = window.innerWidth;
    }
    canvasHeight = canvas.offsetHeight;
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
  };

  // Updates the current and previous orientation state
  const setOrientation = () => {
    previousOrientation = orientation;
    if (typeof window !== 'undefined') {
      orientation = window.matchMedia('(orientation: portrait)').matches
        ? 'portrait'
        : 'landscape';
    }
  };

  // Handles scroll-driven frame switching with requestAnimationFrame throttling
  const scrollEvent = () => {
    if (!wrapper || !allowScrollPlayback || ticking) return;

    ticking = true;

    requestAnimationFrame(() => {
      const { top, height } = wrapper!.getBoundingClientRect();
      const windowHeight =
        typeof window !== 'undefined' ? window.innerHeight : 900;
      const scrollFraction = (-top + windowHeight) / height;

      frameIndex = Math.max(
        1,
        Math.min(frameCount, Math.ceil(scrollFraction * frameCount)),
      );
      updateImage();
      ticking = false;
    });
  };

  // Handles resize and orientation changes
  const resizeEvent = () => {
    const prevWidth = canvasWidth;
    const prevOrientation = orientation;

    setCanvasSize();
    setOrientation();

    // On desktop or real orientation change or real width change on mobile
    if (
      !isTouchDevice ||
      (isTouchDevice && prevWidth !== canvasWidth) ||
      orientation !== prevOrientation
    ) {
      if (orientation !== prevOrientation) {
        // Clear and reset for new orientation frame set
        hasPreloaded = false;
        allowScrollPlayback = false;
        loadedImageCount = 0;
        totalToLoad = 0;
        Object.keys(images).forEach((key) => delete images[key]);

        preloadFirstImage();
        preloadImages();
      } else {
        requestAnimationFrame(updateImage);
      }
    }
  };

  const startListeners = () => {
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', scrollEvent);
      window.addEventListener('resize', resizeEvent);
    }
  };

  const stopListeners = () => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', scrollEvent);
      window.removeEventListener('resize', resizeEvent);
    }
  };

  // Sets up IntersectionObservers to lazily load images and bind/unbind listeners
  const init = () => {
    wrapper = wrapperRef.value;
    canvas = canvasRef.value;
    if (!wrapper || !canvas) return;
    context = canvas.getContext('2d');
    setCanvasSize();
    setOrientation();

    // Start preloading images once component is halfway visible
    preloadObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          preloadImages();
        }
      },
      { rootMargin: settings.preloadRootMargin },
    );

    // Bind scroll listeners only while component is in view
    scrollObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startListeners();
        } else {
          stopListeners();
        }
      },
      { rootMargin: '100px' },
    );

    preloadObserver.observe(wrapper);
    scrollObserver.observe(wrapper);
  };

  // Vue lifecycle hooks
  onMounted(() => {
    if (!process.client) return;

    isTouchDevice = 'ontouchstart' in document.documentElement;

    wrapper = wrapperRef.value;
    canvas = canvasRef.value;
    if (!wrapper || !canvas) return;

    context = canvas.getContext('2d');
    setCanvasSize();
    setOrientation();
    preloadFirstImage(); // Draw first frame immediately
    init(); // Setup observers and listeners
  });

  onBeforeUnmount(() => {
    if (preloadObserver) preloadObserver.disconnect();
    if (scrollObserver) scrollObserver.disconnect();
    stopListeners();
  });
};
